/**
 * VIVANA × IT GEEKS — Mandatory variant selection (PDP)
 *
 * For products with a REAL multi-variant choice (2+ visible, selectable option
 * values), nothing is pre-selected on a fresh product-page load:
 *   - all variant inputs are deselected
 *   - the hidden variant input (name="id") is cleared (no fallback variant)
 *   - the price shows a "From …" state
 *   - Buy Now (accelerated checkout) and the Pack Options block are hidden
 *   - clicking Add to Cart with nothing chosen shows a validation message
 * Once the customer picks a variant, Horizon's native variant-update flow
 * repopulates everything (id, price, gallery, Buy Now); we just reveal packs and
 * clear the "awaiting" state.
 *
 * Only runs on a fresh landing (no ?variant= in the URL). Deep links with an
 * explicit variant, and products whose only choice is hidden/auto-managed (e.g.
 * a lone Jar with a hidden Doypack), are left on Horizon's default behaviour.
 *
 * Owned file — safe across Horizon updates. Loaded (deferred) from theme.liquid.
 */

/**
 * @typedef {{ type: 'radio', el: HTMLFieldSetElement } | { type: 'select', el: HTMLSelectElement }} ChoiceGroup
 */

/**
 * @typedef {Object} State
 * @property {boolean} active
 * @property {Element | null} scope
 * @property {Element | null} picker
 * @property {HTMLFormElement | null} form
 * @property {HTMLInputElement | null} idInput
 * @property {HTMLElement | null} message
 * @property {HTMLElement | null} messageBelow
 * @property {HTMLInputElement | null} sentinel
 */

(function () {
  /** @type {VivanaVariantRequiredConfig} */
  var CFG = window.VIVANA_VARIANT_REQUIRED || {};
  var MESSAGE = CFG.message || 'Please select a variant before adding to cart.';
  // Template with a {option} placeholder filled by the actual product option
  // label (e.g. "Flavor", "Size"). Falls back to MESSAGE when no label is found.
  var MESSAGE_TEMPLATE = CFG.messageTemplate || 'Please select a {option} before adding to cart.';

  var DEBUG = (function () {
    try {
      return /[?&]vivana_debug=1/.test(location.search) || localStorage.getItem('vivana_debug') === '1';
    } catch (e) { return false; }
  })();
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[Vivana variant-required]'].concat([].slice.call(arguments)));
  }

  /** @type {State} */
  var state = { active: false, scope: null, picker: null, form: null, idInput: null, message: null, messageBelow: null, sentinel: null };

  /** @param {() => void} fn */
  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  function injectStyles() {
    if (document.getElementById('vivana-variant-required-style')) return;
    var s = document.createElement('style');
    s.id = 'vivana-variant-required-style';
    s.textContent =
      // Neutralise the selected-pill / selected styling while nothing is chosen
      // (prevents a flash of a pre-selected option before this script runs).
      '[data-vivana-awaiting-variant] .variant-option__button-label__pill{opacity:0 !important;}' +
      '[data-vivana-awaiting-variant] .variant-option__button-label:has(:checked){' +
      'border-color:var(--color-variant-border) !important;color:var(--color-variant-text) !important;}' +
      // Prominent bold validation message directly above Add to Cart.
      '.vivana-variant-required-msg{display:block;width:100%;' +
      'margin-block:1rem .85rem;' +
      'color:var(--color-error,#c0392b);font-size:1.0625rem;font-weight:700;line-height:1.4;}' +
      '.vivana-variant-required-msg[hidden]{display:none;}' +
      // Copy shown directly under the option group sits tighter to the options.
      '.vivana-variant-required-msg--below{margin-block:.5rem 0;}' +
      // Error state (set after a blocked add): recolour the EXISTING option-pill /
      // dropdown border to the error colour only — same thickness/style as default,
      // no extra ring. Auto-cleared ~1.5s later.
      '[data-vivana-variant-error] .variant-option:not(.variant-option--vivana-hidden) .variant-option__button-label{' +
      'border-color:var(--color-error,#c0392b) !important;}' +
      '[data-vivana-variant-error] .variant-option:not(.variant-option--vivana-hidden) select{' +
      'border-color:var(--color-error,#c0392b) !important;}';
    document.head.appendChild(s);
  }

  /**
   * Option groups that represent a REAL customer choice: 2+ visible, selectable
   * values. Hidden/auto-managed rows (variant-option--vivana-hidden) and
   * single-value groups are ignored, so single-choice products keep working.
   *
   * @param {Element} picker
   * @returns {ChoiceGroup[]}
   */
  function realChoiceGroups(picker) {
    /** @type {ChoiceGroup[]} */
    var groups = [];
    picker.querySelectorAll('fieldset').forEach(function (fs) {
      if (fs.classList.contains('variant-option--vivana-hidden')) return;
      if (fs.querySelectorAll('input[type="radio"]').length >= 2) groups.push({ type: 'radio', el: fs });
    });
    picker.querySelectorAll('.variant-option--dropdowns').forEach(function (wrap) {
      if (wrap.classList.contains('variant-option--vivana-hidden')) return;
      var sel = wrap.querySelector('select');
      // A dropdown with a "Choose…" placeholder has 1 extra option; require 2 real ones.
      if (sel && sel.querySelectorAll('option:not([data-vivana-placeholder])').length >= 2) {
        groups.push({ type: 'select', el: sel });
      }
    });
    return groups;
  }

  /**
   * True when every real-choice group has an explicit user selection.
   * @param {Element} picker
   */
  function isComplete(picker) {
    var groups = realChoiceGroups(picker);
    if (!groups.length) return true;
    return groups.every(function (g) {
      if (g.type === 'radio') return !!g.el.querySelector('input[type="radio"]:checked');
      var sel = g.el;
      var opt = sel.options[sel.selectedIndex];
      return !!opt && !opt.disabled && !opt.hasAttribute('data-vivana-placeholder') && sel.value !== '';
    });
  }

  /**
   * While awaiting a choice, make the form fail HTML constraint validation so
   * Horizon's AddToCartComponent.handleClick() bails at its own
   * `if (!form.checkValidity()) return;` guard (product-form.js). That guard sits
   * BEFORE the fly-to-cart + spinner/checkmark animation, so the misleading
   * "added" UI never fires — even though Horizon's delegated capture-phase click
   * listener runs before ours. A hidden, unnamed, required sentinel input makes
   * checkValidity() return false (only type=hidden/disabled controls are barred
   * from validation, so a display:none input still counts and is never
   * submitted). form.noValidate is set so a stray Enter-key submit can't surface
   * a native validation bubble; our capture-phase submit handler shows the
   * message instead.
   */
  function blockNativeSubmit() {
    var form = state.form;
    if (!form) return;
    if (!state.sentinel) {
      var s = document.createElement('input');
      s.type = 'text';
      s.required = true;
      s.value = '';
      s.tabIndex = -1;
      s.setAttribute('aria-hidden', 'true');
      s.className = 'vivana-variant-required-sentinel';
      s.style.display = 'none';
      form.appendChild(s);
      state.sentinel = s;
    }
    if (!form.hasAttribute('data-vivana-had-novalidate')) {
      form.setAttribute('data-vivana-had-novalidate', form.noValidate ? '1' : '0');
    }
    form.noValidate = true;
  }

  function unblockNativeSubmit() {
    if (state.sentinel && state.sentinel.parentNode) state.sentinel.parentNode.removeChild(state.sentinel);
    state.sentinel = null;
    var form = state.form;
    if (form && form.hasAttribute('data-vivana-had-novalidate')) {
      form.noValidate = form.getAttribute('data-vivana-had-novalidate') === '1';
      form.removeAttribute('data-vivana-had-novalidate');
    }
  }

  function enterAwaiting() {
    var picker = state.picker;
    var scope = state.scope;
    if (!picker || !scope) return;

    // Deselect radios.
    /** @type {NodeListOf<HTMLInputElement>} */ (
      picker.querySelectorAll('input[type="radio"]')
    ).forEach(function (r) {
      r.checked = false;
      r.removeAttribute('checked');
      if (r.dataset) r.dataset.currentChecked = 'false';
    });

    // Deselect dropdowns: prepend a disabled "Choose…" placeholder and select it.
    realChoiceGroups(picker).forEach(function (g) {
      if (g.type !== 'select') return;
      var sel = g.el;
      if (!sel.querySelector('option[data-vivana-placeholder]')) {
        var opt = document.createElement('option');
        opt.value = '';
        opt.textContent = CFG.chooseLabel || 'Choose…';
        opt.disabled = true;
        opt.setAttribute('data-vivana-placeholder', '');
        sel.insertBefore(opt, sel.firstChild);
      }
      sel.value = '';
      sel.selectedIndex = 0;
    });

    // Clear the variant id so no fallback variant can be submitted.
    var idInput = state.idInput;
    if (idInput) idInput.value = '';

    // Suppress Horizon's fly-to-cart / spinner / checkmark animation while
    // nothing is chosen (its handleClick bails on an invalid form).
    blockNativeSubmit();

    // Hide Buy Now (accelerated checkout) while nothing is chosen. The Pack Options
    // widget stays VISIBLE. We deliberately leave the native product price alone so
    // it keeps showing the default variant's price until the customer chooses — the
    // pack block drives price changes as supply options are selected.
    var acc = scope.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (acc) acc.setAttribute('hidden', '');

    (picker.closest('.shopify-section') || scope).setAttribute('data-vivana-awaiting-variant', '');
    state.active = true;
    log('awaiting — id cleared; checked now:', picker.querySelectorAll('input:checked').length);
  }

  function exitAwaiting() {
    if (!state.active) return;
    var picker = state.picker;
    var scope = state.scope;
    if (!picker || !scope) return;
    (picker.closest('.shopify-section') || scope).removeAttribute('data-vivana-awaiting-variant');
    unblockNativeSubmit();
    // Let the pack block re-sync the price to the now-selected variant (native for
    // 1-Month, discounted preview for 3/6-Month).
    document.dispatchEvent(new CustomEvent('vivana:awaiting-cleared'));
    // Buy Now is re-revealed by Horizon's own variant-update handler.
    hideMessage();
    state.active = false;
    var idInput = state.idInput;
    log('selected — variant id:', idInput && idInput.value);
  }

  /**
   * Position (or re-position) the message directly above the Add-to-Cart button
   * row. `.product-form-buttons` holds the quantity selector + Add to Cart and
   * sits right after the pack options, so inserting immediately before it places
   * the message below the variant selectors / pack options and directly above the
   * Add to Cart button — full width, on its own line. Re-runs on every show so a
   * message created earlier (before packs relocated) still lands correctly.
   * @param {HTMLElement} message
   */
  function placeMessageAboveButtons(message) {
    var form = state.form;
    var scope = state.scope;
    var row = (form && form.querySelector('.product-form-buttons')) ||
              (scope && scope.querySelector('.product-form-buttons'));
    if (row && row.parentNode) {
      if (message.nextElementSibling !== row) row.parentNode.insertBefore(message, row);
    } else if (form && !message.parentNode) {
      form.appendChild(message);
    }
  }

  /** Place (or move) the message directly below the first still-missing option group. */
  function placeMessageBelowGroup(message) {
    var picker = state.picker;
    if (!picker) return;
    var g = firstIncompleteGroup(picker) || realChoiceGroups(picker)[0];
    var anchor = g && g.el;
    if (anchor && anchor.parentNode) {
      if (anchor.nextElementSibling !== message) anchor.parentNode.insertBefore(message, anchor.nextSibling);
    } else if (!message.parentNode) {
      picker.appendChild(message);
    }
  }

  /** Strip whitespace and any trailing ": value" so we get just the option name. */
  function cleanOptionName(raw) {
    if (!raw) return '';
    var t = String(raw).replace(/\s+/g, ' ').trim();
    var colon = t.indexOf(':');
    if (colon > 0) t = t.slice(0, colon).trim();
    return t;
  }

  /**
   * The product's actual option label for a choice group (e.g. "Flavor", "Size",
   * "Collagen pro Flavor") — read from the radio fieldset's <legend> or, for a
   * dropdown, its associated <label>.
   * @param {ChoiceGroup} group
   */
  function optionNameFromGroup(group) {
    if (!group || !group.el) return '';
    var el = group.el;
    var legend = el.querySelector && el.querySelector('legend');
    var raw = (legend && legend.textContent) || '';
    if (!raw && el.closest) {
      var wrap = el.closest('.variant-option--dropdowns');
      var lbl = wrap && wrap.querySelector('label');
      raw = (lbl && lbl.textContent) || '';
    }
    if (!raw && el.getAttribute) raw = el.getAttribute('data-option-name') || el.getAttribute('aria-label') || '';
    return cleanOptionName(raw);
  }

  /**
   * First real-choice group with no explicit selection — drives the "show the
   * first missing option" behaviour for multi-option products.
   * @param {Element} picker
   * @returns {ChoiceGroup | null}
   */
  function firstIncompleteGroup(picker) {
    var groups = realChoiceGroups(picker);
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i], done;
      if (g.type === 'radio') {
        done = !!g.el.querySelector('input[type="radio"]:checked');
      } else {
        var sel = g.el, opt = sel.options[sel.selectedIndex];
        done = !!opt && !opt.disabled && !opt.hasAttribute('data-vivana-placeholder') && sel.value !== '';
      }
      if (!done) return g;
    }
    return null;
  }

  /** Build the message text from the actual label of the first missing option. */
  function currentMessageText() {
    var picker = state.picker;
    var g = picker && firstIncompleteGroup(picker);
    var name = g ? optionNameFromGroup(g) : '';
    return name ? MESSAGE_TEMPLATE.replace('{option}', name) : MESSAGE;
  }

  /**
   * @param {string} [extraClass]
   * @returns {HTMLElement}
   */
  function makeMessageEl(extraClass) {
    var m = document.createElement('div');
    m.className = 'vivana-variant-required-msg' + (extraClass ? ' ' + extraClass : '');
    m.setAttribute('role', 'alert');
    return m;
  }

  var errorResetTimer = 0;

  // Two PERSISTENT messages — (A) directly below the first missing option group
  // and (B) above the Add to Cart button — both stay until a valid variant is
  // chosen. Only the red pill borders are a brief attention cue that auto-clears
  // after ~1.5s.
  function showMessage() {
    var text = currentMessageText();

    if (!state.message) state.message = makeMessageEl();
    state.message.textContent = text;
    placeMessageAboveButtons(state.message);
    state.message.hidden = false;

    if (!state.messageBelow) state.messageBelow = makeMessageEl('vivana-variant-required-msg--below');
    state.messageBelow.textContent = text;
    placeMessageBelowGroup(state.messageBelow);
    state.messageBelow.hidden = false;

    setErrorState(true);
    if (errorResetTimer) clearTimeout(errorResetTimer);
    errorResetTimer = setTimeout(clearErrorBorders, 1500);
  }

  /** Clear ONLY the red pill borders (the ~1.5s attention cue). Messages remain. */
  function clearErrorBorders() {
    if (errorResetTimer) { clearTimeout(errorResetTimer); errorResetTimer = 0; }
    setErrorState(false);
  }

  /** Refresh the visible message(s) (text + below-group position) while awaiting. */
  function updateMessages() {
    if (!state.message || state.message.hidden) return;
    var text = currentMessageText();
    state.message.textContent = text;
    if (state.messageBelow && !state.messageBelow.hidden) {
      state.messageBelow.textContent = text;
      placeMessageBelowGroup(state.messageBelow);
    }
  }

  function hideMessage() {
    if (state.message) state.message.hidden = true;
    if (state.messageBelow) state.messageBelow.hidden = true;
    clearErrorBorders();
  }

  /**
   * Toggle the variant error state (red pill borders). Set on a blocked add,
   * cleared as soon as a valid variant is chosen. Applied to the same section
   * element that carries the awaiting flag.
   * @param {boolean} on
   */
  function setErrorState(on) {
    var el = (state.picker && state.picker.closest('.shopify-section')) || state.scope;
    if (!el || !el.setAttribute) return;
    if (on) el.setAttribute('data-vivana-variant-error', '');
    else el.removeAttribute('data-vivana-variant-error');
  }

  /** Height of the sticky header (content beneath it is what's actually visible). */
  function stickyHeaderHeight() {
    var h = 0;
    try {
      h = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    } catch (e) { /* noop */ }
    if (!h) {
      var header = document.querySelector('header-component, .header-section, #header-group');
      if (header) h = header.getBoundingClientRect().height || 0;
    }
    return h;
  }

  /** Scroll offset: header height + a little breathing room below it. */
  function headerOffset() {
    return stickyHeaderHeight() + 20;
  }

  /**
   * True when most of the element is already on screen (below the sticky header,
   * above the fold). Used to skip a needless scroll when the selector is visible.
   * @param {Element} el
   */
  function isMostlyVisible(el) {
    if (!el || !el.getBoundingClientRect) return false;
    var rect = el.getBoundingClientRect();
    if (rect.height <= 0) return false;
    var top = stickyHeaderHeight();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, top));
    return visible / rect.height >= 0.75; // ≥75% visible → treat as "already visible"
  }

  /**
   * Smoothly scroll the first real-choice variant group into view (below the
   * sticky header), so a blocked add points the customer straight at what they
   * must choose. Skips scrolling entirely when the selector is already mostly
   * visible, so the page doesn't jump when the customer can already see it.
   */
  function scrollToVariant() {
    var picker = state.picker;
    if (!picker) return;
    var groups = realChoiceGroups(picker);
    var group = groups[0];
    if (!group) return;
    var target = group.type === 'select'
      ? (group.el.closest('.variant-option--dropdowns') || group.el)
      : group.el;
    if (!target || !target.getBoundingClientRect) return;
    // Already on screen → keep the customer where they are, just show the message.
    if (isMostlyVisible(target)) { log('variant selector already visible — no scroll'); return; }
    var top = window.pageYOffset + target.getBoundingClientRect().top - headerOffset();
    if (top < 0) top = 0;
    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { /* noop */ }
    try { window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' }); }
    catch (e) { window.scrollTo(0, top); }
  }

  function onChange() {
    // Let Horizon's fetch/morph + variant-update settle, then re-evaluate.
    requestAnimationFrame(function () { setTimeout(check, 60); });
  }

  var checkTries = 0;
  function check() {
    if (!state.active) return;
    var picker = state.picker;
    if (!picker) return;
    if (!isComplete(picker)) { checkTries = 0; updateMessages(); return; }
    // Wait for Horizon to populate the variant id (a few retries).
    var idInput = state.idInput;
    if (idInput && !idInput.value && checkTries < 8) { checkTries++; setTimeout(check, 80); return; }
    checkTries = 0;
    exitAwaiting();
  }

  /** @param {Event} e */
  function onClickCapture(e) {
    if (!state.active) return;
    var t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    var btn = t.closest('button[name="add"], .add-to-cart-button, [ref="addToCartButton"]');
    var form = state.form;
    var picker = state.picker;
    var idInput = state.idInput;
    if (!btn || !form || !form.contains(btn)) return;
    if (!picker || !isComplete(picker) || !idInput || !idInput.value) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showMessage();
      scrollToVariant();
      log('blocked Add to Cart — no variant selected');
    }
  }

  function init() {
    var picker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!picker) { log('no main-product variant picker — skipping'); return; }

    var scope = picker.closest('.shopify-section') || document.documentElement;
    var form = /** @type {HTMLFormElement | null} */ (
      scope.querySelector('product-form-component form[action*="/cart/add"]') ||
      scope.querySelector('form[action*="/cart/add"]')
    );

    state.picker = picker;
    state.scope = scope;
    state.form = form;
    state.idInput = form && /** @type {HTMLInputElement | null} */ (form.querySelector('[name="id"]'));

    injectStyles();

    // Only a genuine multi-variant choice triggers mandatory selection.
    if (!realChoiceGroups(picker).length) { log('single real choice (or none) — Horizon default kept'); return; }

    // A ?variant in the URL only counts as a deliberate choice when it's a
    // NON-default variant (a genuine shared/deep link). Storefront links auto-
    // append ?variant=<default/first> (product cards, menus, bundle links), which
    // must still trigger mandatory selection — otherwise the first variant stays
    // selected. So skip only for an explicit non-default variant.
    var urlVariant = new URLSearchParams(location.search).get('variant');
    if (urlVariant) {
      var defaults = (CFG.defaultVariantIds || []).map(String);
      if (defaults.indexOf(String(urlVariant)) === -1) {
        log('explicit non-default ?variant — selection kept', urlVariant);
        return;
      }
      log('?variant is the default/first — entering awaiting anyway', urlVariant);
    }

    log('init', {
      initialId: state.idInput && state.idInput.value,
      initialChecked: Array.from(/** @type {NodeListOf<HTMLInputElement>} */ (picker.querySelectorAll('input:checked'))).map(function (r) { return r.value; }),
    });

    enterAwaiting();

    picker.addEventListener('change', onChange);
    document.addEventListener('click', onClickCapture, true);
    if (form) {
      form.addEventListener('submit', function (e) {
        if (state.active && (!state.idInput || !state.idInput.value)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showMessage();
          scrollToVariant();
          log('blocked submit — no variant selected');
        }
      }, true);
    }
  }

  ready(init);
})();
